// Central tool registry — shared between Landing, Dashboard, ToolPage
import {
  MagnifyingGlass,
  Megaphone,
  Hash,
  ChatCircleDots,
  MapPin,
  EyeSlash,
  Rocket,
  Buildings,
  TrendUp,
  Heart,
  LinkSimple,
} from "@phosphor-icons/react";

export function normalizeToolKey(key) {
  const normalized = String(key || "").trim().toLowerCase();

  const aliases = {
    lifecycle: "relate",
  };

  return aliases[normalized] || normalized;
}

export const TOOLS = [
  {
    key: "seo",
    name: "SEO AI",
    tagline: "Keyword research, meta tags, on-page audit.",
    category: "GROWTH",
    icon: MagnifyingGlass,
    featured: true,
    free: true,
    fields: [
      { name: "website", label: "Website or domain", placeholder: "acme.com", required: true },
      { name: "topic", label: "Primary topic / niche", placeholder: "organic skincare for men" },
      { name: "location", label: "Target location (optional)", placeholder: "United Kingdom" },
    ],
  },
  {
    key: "ads",
    name: "Ads AI",
    tagline: "Google RSA + multi-platform ad copy that converts.",
    category: "GROWTH",
    icon: Megaphone,
    featured: true,
    free: false,
    fields: [
      { name: "product", label: "Product / offer", placeholder: "AI resume builder — £9/mo", required: true },
      { name: "audience", label: "Target audience", placeholder: "job seekers 25-40 in the UK" },
      { name: "usp", label: "Unique selling point", placeholder: "Generate a resume in 30 seconds" },
      { name: "platform", label: "Platform", placeholder: "Google, Meta, Instagram, X, LinkedIn, TikTok" },
    ],
  },
  {
    key: "social",
    name: "Social AI",
    tagline: "A week of scroll-stopping posts in seconds.",
    category: "GROWTH",
    icon: Hash,
    free: false,
    fields: [
      { name: "brand", label: "Brand / creator", placeholder: "Northwind Coffee Co.", required: true },
      { name: "platform", label: "Platform", placeholder: "Instagram, LinkedIn, X, TikTok, Facebook" },
      { name: "tone", label: "Tone of voice", placeholder: "witty, warm, expert" },
      { name: "goal", label: "Content goal", placeholder: "drive DTC sales" },
    ],
  },
  {
    key: "reviews",
    name: "Reviews AI",
    tagline: "Reply to every review — 10× faster, on-brand.",
    category: "GROWTH",
    icon: ChatCircleDots,
    free: false,
    fields: [
      { name: "business", label: "Business name", placeholder: "Northwind Coffee", required: true },
      { name: "customer_name", label: "Customer name", placeholder: "Sarah Johnson" },
      { name: "rating", label: "Rating", placeholder: "5" },
      { name: "review", label: "The review", type: "textarea", placeholder: "Paste the customer review...", required: true },
      { name: "tone", label: "Desired tone", placeholder: "warm, apologetic, solution-oriented" },
    ],
  },
  {
    key: "local",
    name: "Local AI",
    tagline: "Dominate local search and Google Business Profile.",
    category: "GROWTH",
    icon: MapPin,
    free: false,
    fields: [
      { name: "business", label: "Business name", placeholder: "Elm Street Dentistry", required: true },
      { name: "city", label: "City / service area", placeholder: "London, UK", required: true },
      { name: "service", label: "Main service", placeholder: "cosmetic dentistry" },
    ],
  },
  {
    key: "brand",
    name: "Brand Monitoring",
    tagline: "Track sentiment, mentions, and reputation risk.",
    category: "GROWTH",
    icon: EyeSlash,
    free: false,
    fields: [
      { name: "brand", label: "Brand to monitor", placeholder: "Acme Inc.", required: true },
      { name: "signals", label: "Recent signals / mentions", type: "textarea", placeholder: "Paste review snippets, tweets, or news headlines..." },
    ],
  },
  {
    key: "starter",
    name: "Business Starter Kit",
    tagline: "Value prop, persona, pricing, 90-day launch plan.",
    category: "FOUNDATION",
    icon: Rocket,
    free: true,
    fields: [
      { name: "idea", label: "Business idea", placeholder: "marketplace for local makers", required: true },
      { name: "budget", label: "Starting budget (GBP)", placeholder: "5000" },
      { name: "location", label: "Where you're starting", placeholder: "London, UK" },
    ],
  },
  {
    key: "formation",
    name: "Business Formation",
    tagline: "Ltd vs Sole Trader, Companies House, HMRC, compliance calendar.",
    category: "FOUNDATION",
    icon: Buildings,
    free: false,
    fields: [
      { name: "business_type", label: "What you're building", placeholder: "SaaS company", required: true },
      { name: "trading_region", label: "Trading region", placeholder: "England & Wales" },
      { name: "owners", label: "Number of owners / directors", placeholder: "2" },
      { name: "expected_turnover", label: "Expected first-year turnover (GBP)", placeholder: "£80,000" },
    ],
  },
  {
    key: "growth",
    name: "Growth Tools",
    tagline: "North-star metric, loops, channels, experiments.",
    category: "STRATEGY",
    icon: TrendUp,
    free: false,
    fields: [
      { name: "product", label: "Product", placeholder: "B2B analytics SaaS", required: true },
      { name: "stage", label: "Stage", placeholder: "post-MVP, £20k MRR" },
      { name: "goal", label: "90-day goal", placeholder: "3x MRR" },
    ],
  },
  {
    key: "relate",
    name: "Relate Marketing Suite",
    tagline: "Lifecycle, CRM, loyalty and retention flows.",
    category: "STRATEGY",
    icon: Heart,
    free: false,
    fields: [
      { name: "brand", label: "Brand", placeholder: "Northwind Coffee Co.", required: true },
      { name: "customers", label: "Current customers", placeholder: "~8k subscribers" },
      { name: "channel", label: "Primary channel", placeholder: "email + SMS" },
    ],
  },
  {
    key: "source",
    name: "Go to Source",
    tagline: "Traffic attribution, UTM taxonomy, dashboards.",
    category: "STRATEGY",
    icon: LinkSimple,
    free: false,
    fields: [
      { name: "url", label: "Website URL", placeholder: "https://acme.com", required: true },
      { name: "channels", label: "Channels you run", placeholder: "Google Ads, SEO, newsletter" },
    ],
  },
];

export const OUTPUT_CHANNEL_OPTIONS = [
  { value: "linkedin", label: "LinkedIn Post", hint: "Professional, thought-leadership style post for LinkedIn." },
  { value: "wordpress", label: "WordPress Blog", hint: "Longer-form blog or draft content for WordPress." },
  { value: "newsletter", label: "Newsletter", hint: "Email subject, preview text and body copy." },
];

export const getOutputChannelByValue = (value) => {
  const normalized = String(value || "linkedin").trim().toLowerCase();

  if (["wordpress", "wordpress_blog", "wordpress blog", "blog", "wp"].includes(normalized) || normalized.includes("wordpress") || normalized.includes("blog")) {
    return OUTPUT_CHANNEL_OPTIONS.find((item) => item.value === "wordpress");
  }

  if (["newsletter", "email", "email newsletter"].includes(normalized) || normalized.includes("newsletter") || normalized.includes("email")) {
    return OUTPUT_CHANNEL_OPTIONS.find((item) => item.value === "newsletter");
  }

  if (["linkedin", "linkedin post", "linkedin_post"].includes(normalized) || normalized.includes("linkedin")) {
    return OUTPUT_CHANNEL_OPTIONS.find((item) => item.value === "linkedin");
  }

  // Old records may contain multi_channel, instagram, meta, x, or a free-text platform value.
  // For v2 we only support LinkedIn, WordPress and Newsletter, so default safely to LinkedIn.
  return OUTPUT_CHANNEL_OPTIONS.find((item) => item.value === "linkedin");
};

export const FREE_TOOLS = TOOLS.filter((t) => t.free).map((t) => t.key);

export const getToolByKey = (key) => {
  const normalized = normalizeToolKey(key);
  return TOOLS.find((t) => normalizeToolKey(t.key) === normalized);
};

export const getToolNameByKey = (key) => {
  const tool = getToolByKey(key);
  return tool?.name || key;
};